import type { Transaction } from '@familywallet/shared';
import {
  ArrowDown,
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUp,
  ArrowUpRight,
  Landmark,
  Repeat,
  Undo2,
  Wallet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { formatAmount } from '../lib/money';
import { cn } from '../lib/utils';
import { IconSquare, listRowClass } from './ui/card';

const ICONS = {
  expense: ArrowDown,
  income: ArrowUp,
  transfer: ArrowLeftRight,
  opening_balance: Wallet,
  reversal: Undo2,
  // Debts (Phase 4): money in or out, never income or expense.
  borrow: ArrowDownLeft,
  lend: ArrowUpRight,
  repay: ArrowUpRight,
  collect: ArrowDownLeft,
  debt_opening: Landmark,
  exchange: Repeat,
} as const;

/** Names of the transaction's categories, in line order, without repeats. */
function categoryNames(tx: Transaction): string[] {
  return [
    ...new Set(
      tx.lines.flatMap((l) => (l.role === 'category' && l.accountName ? [l.accountName] : [])),
    ),
  ];
}

/**
 * What arrived in an exchange: its FX trading line that gave the currency out (never masked).
 */
export function exchangeReceived(tx: Transaction): { amount: string; currency: string } | null {
  const line = tx.lines.find(
    (l) => l.role === 'other' && l.amount !== null && l.amount.startsWith('-'),
  );
  return line ? { amount: line.amount!.slice(1), currency: line.currency } : null;
}

/**
 * One transaction in a list (docs/DESIGN.md §4 list group row): icon, title (payee, else
 * description, else category), "Category · Account" underneath, amount on the end side.
 * Expenses read in the normal text color, income in accent; amounts in another currency show
 * their base-currency value from the transaction's own rate underneath.
 */
export function TransactionRow({ tx, baseCurrency }: { tx: Transaction; baseCurrency: string }) {
  const { t, i18n } = useTranslation();
  const Icon = ICONS[tx.kind];
  const categories = categoryNames(tx);
  const money = tx.lines.filter((l) => l.role === 'money');
  const accountName = (l: (typeof money)[number]) => l.accountName ?? t('activity.hiddenAccount');
  let accounts: string;
  if (
    tx.kind === 'transfer' ||
    (tx.kind === 'exchange' && new Set(money.map(accountName)).size > 1)
  ) {
    const to = money.find((l) => l.amount === null || !l.amount.startsWith('-'));
    const from = money.find((l) => l !== to);
    accounts = t('activity.fromTo', {
      from: from ? accountName(from) : '',
      to: to ? accountName(to) : '',
    });
  } else {
    accounts = [...new Set(money.map(accountName))].join(', ');
  }
  // A debt reads for the viewer: "Borrowed · My cash" under the person's name.
  const debtLabel = tx.debt ? t(`debts.labels.${tx.debt.label}`) : null;
  const title = tx.debt
    ? tx.debt.personName
    : tx.payee?.name || tx.description || categories.join(', ') || t(`activity.kinds.${tx.kind}`);
  const subtitle = (
    tx.debt
      ? [debtLabel, accounts]
      : [tx.payee || tx.description ? categories.join(', ') : '', accounts]
  )
    .filter(Boolean)
    .join(' · ');

  let shown: string | null = null;
  let tone = 'text-fg';
  if (tx.amount !== null) {
    if (tx.kind === 'expense') {
      shown = formatAmount(`-${tx.amount}`, tx.currency, i18n.language);
    } else if (tx.kind === 'income') {
      shown = formatAmount(tx.amount, tx.currency, i18n.language, { signed: true });
      tone = 'text-accent';
    } else {
      shown = formatAmount(tx.amount, tx.currency, i18n.language);
    }
  }
  const base =
    tx.currency !== baseCurrency && tx.baseAmount !== null
      ? formatAmount(tx.baseAmount, baseCurrency, i18n.language)
      : null;
  // An exchange shows what arrived underneath what left.
  const received = tx.kind === 'exchange' ? exchangeReceived(tx) : null;

  return (
    <Link to={`/activity/${tx.id}`} className={listRowClass}>
      <IconSquare>
        <Icon
          className={cn(
            'size-5',
            tx.kind === 'expense' ? 'text-expense' : tx.kind === 'income' ? 'text-accent' : '',
          )}
          strokeWidth={2}
        />
      </IconSquare>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[15px] font-medium">{title}</span>
        {subtitle && <span className="truncate text-xs text-muted">{subtitle}</span>}
        {tx.reversedByTransactionId && (
          <span className="text-xs text-muted">{t('activity.corrected')}</span>
        )}
        {tx.sharedWithFamily && (
          <span className="text-xs text-muted">{t('activity.sharedWithFamily')}</span>
        )}
        {tx.estimatedWithFamilyRate && (
          <span className="text-xs text-muted">{t('foreign.estimated')}</span>
        )}
        {tx.recurring?.auto && (
          <span className="text-xs text-muted">{t('recurring.recordedAutomatically')}</span>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end">
        {shown && (
          <span dir="ltr" className={cn('text-[15px] font-semibold tabular-nums', tone)}>
            {shown}
          </span>
        )}
        {received ? (
          <span dir="ltr" className="text-xs text-muted tabular-nums">
            → {formatAmount(received.amount, received.currency, i18n.language)}
          </span>
        ) : (
          base && (
            <span dir="ltr" className="text-xs text-muted tabular-nums">
              ≈ {base}
            </span>
          )
        )}
      </div>
    </Link>
  );
}
